"use client";

import { formatNumberWithCommas } from "@alba-cars/common-modules";
import clsx from "clsx";
import { Control, Controller, Path, PathValue } from "react-hook-form";

import { cn } from "@/lib/utils";

interface FormTextFieldProps<T extends Record<string, any>> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  capitalise?: boolean;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
  type?: "text" | "password" | "email" | "number";
  disabled?: boolean;
  formatNumber?: boolean;
  allowDecimals?: boolean;
  className?: string;
  containerClassName?: string;
  useCustomValidation?: boolean;
  validate?: (value: PathValue<T, Path<T>>) => boolean | string;
  inputMode?: "decimal" | "email" | "numeric" | "search" | "tel" | "text" | "url" | "none";
  [key: string]: any;
}

const formatInput = (value: string, allowDecimals: boolean): string => {
  // Allow decimal points if enabled
  const regex = allowDecimals ? /[^\d.]/g : /[^\d]/g;
  const cleanedValue = value.replace(regex, "");

  // Ensure only one decimal point
  if (allowDecimals) {
    const parts = cleanedValue.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join("");
    }
  }

  if (!cleanedValue) return "";

  // If decimal exists, format only the integer part
  if (allowDecimals && cleanedValue.includes(".")) {
    const [integerPart, decimalPart] = cleanedValue.split(".");
    return `${formatNumberWithCommas(integerPart)}.${decimalPart}`;
  }

  return formatNumberWithCommas(cleanedValue);
};

export default function FormTextField<T extends Record<string, any>>({
  control,
  name,
  label,
  capitalise,
  required,
  maxLength = 255,
  placeholder,
  type = "text",
  disabled,
  formatNumber = false,
  allowDecimals = false,
  className = "",
  containerClassName = "",
  useCustomValidation = false,
  validate,
  inputMode,
  ...props
}: FormTextFieldProps<T>) {
  const finalPlaceholder = required ? `${placeholder} *` : placeholder;

  if (useCustomValidation && !validate) {
    throw new Error("validate is required when useCustomValidation is true");
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={
        useCustomValidation
          ? {
              required: required ? { value: true, message: "This field is required" } : undefined,
              validate: validate,
            }
          : undefined
      }
      render={({ field: { value = "", onChange, ...fieldProps }, fieldState: { error } }) => (
        <div className={cn("w-full", containerClassName)}>
          {label && (
            <label className="inter mb-3 block text-sm font-semibold text-[#000000]">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
          )}
          <div className="relative">
            <input
              {...fieldProps}
              value={formatNumber && type === "number" ? formatInput(String(value), allowDecimals) : value}
              onChange={(e) => {
                let newValue = e.target.value;

                if (formatNumber && type === "number") {
                  // Store the raw number value without commas
                  newValue = e.target.value.replace(/,/g, "");
                  // Convert to number for form state, but allow empty strings
                  onChange(newValue === "" ? "" : Number(newValue));
                } else if (capitalise) {
                  onChange(e.target.value.toUpperCase());
                } else if (type === "number") {
                  // Allow empty string for number inputs, convert only non-empty values
                  const value = e.target.value;
                  onChange(value === "" ? "" : Number(value));
                } else {
                  onChange(e.target.value);
                }
              }}
              type={formatNumber ? "text" : type}
              placeholder={finalPlaceholder}
              maxLength={maxLength}
              disabled={disabled}
              inputMode={type === "number" && !inputMode ? "decimal" : (inputMode ?? "text")}
              className={clsx(
                "block w-full rounded-2xl bg-secondary-light",
                "min-h-[42px] px-6 py-4",
                "inter text-sm font-medium text-gray-800",
                "border border-secondary-light",
                error && "border-red-500",
                !error && "hover:border-grey-border",
                "focus:outline-none",
                error
                  ? "focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  : "focus:border-grey-border focus:ring-1 focus:ring-grey-border",
                "transition-all duration-300",
                disabled && "cursor-not-allowed bg-gray-100 opacity-75",
                className,
              )}
              {...props}
            />
          </div>
          {error && <div className="inter mt-1 text-sm font-semibold text-red-500">{error.message}</div>}
        </div>
      )}
    />
  );
}
